//
import type { PriceFormData } from '@/types/Components/FormData'
import type { AxiosCollection } from '@/types/Components/Response'
import type { BasePrice, BasePriceWeight, PriceDifference } from '@/types/Models/BasePrice'
import type { PricesStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { ASSIGNMENT, BASE, CUSTOMER, SENDER_ADDRESS } from '@/const/PriceTypes'
import BasePriceProxy from '@/proxies/Price/BasePriceProxy'
import CompanyPriceProxy from '@/proxies/Price/CompanyPriceProxy'
import AssignmentPriceProxy from '@/proxies/Price/AssignmentPriceProxy'
import SenderAddressPriceProxy from '@/proxies/Price/SenderAddressPriceProxy'

const createProxy = (type: string, params?: any) => {
  switch (type) {
    case CUSTOMER:
      return new CompanyPriceProxy()
    case ASSIGNMENT:
      return new AssignmentPriceProxy()
    case SENDER_ADDRESS:
      return new SenderAddressPriceProxy()
    default:
      return new BasePriceProxy(params)
  }
}

export const usePricesStore = defineStore('prices', {
  state: (): PricesStoreState => ({
    changedPrices: [],
    prices: [],
    type: BASE,
    entityId: null,
    differencePrices: {},
  }),
  actions: {
    setPrices(prices: BasePrice[]) {
      this.prices = prices
    },
    setType(type: string) {
      this.type = type
    },
    setEntityId(entityId: string | null) {
      this.entityId = entityId
    },
    updatePrices(price: BasePrice) {
      this.prices = this.prices.map((existing) => (existing.id === price.id ? { ...existing, ...price } : existing))
      const index = this.changedPrices.findIndex((changedPrice) => changedPrice.id === price.id)

      if (index < 0) {
        this.changedPrices.push(price)
      } else {
        this.changedPrices[index] = price
      }
    },
    updateWeightPrices({ priceId, priceWeightData }: { priceId: string; priceWeightData: BasePrice }) {
      const priceIndex = this.prices.findIndex((price) => price.id === priceId)
      if (priceIndex < 0) return

      const price = this.prices[priceIndex]
      if (!price || !price.weights) return

      price.weights = price.weights.map((weight: BasePriceWeight) =>
        priceWeightData.id === weight.id ? ({ ...weight, ...priceWeightData } as BasePriceWeight) : weight,
      )
      this.changedPrices.push({ ...price } as BasePrice)
    },
    resetChangedState() {
      this.changedPrices = []
    },
    resetState() {
      this.prices = []
      this.changedPrices = []
      this.type = ''
      this.entityId = null
    },
    removeFromState(id: string) {
      this.prices = this.prices.filter((price) => price.id !== id)
      this.changedPrices = this.changedPrices.filter((price) => price.id !== id)
    },
    async save() {
      const proxy = createProxy(this.type)
      const data = { prices: toRaw(this.prices) }
      const entityId = `${this.entityId ?? ''}`

      switch (this.type) {
        case CUSTOMER:
        case ASSIGNMENT:
        case SENDER_ADDRESS:
          await proxy.update(entityId, data)
          break
        default:
          await proxy.update(entityId, data)
          break
      }

      this.resetChangedState()

      if (this.type !== BASE) {
        await this.fetch(entityId)
      }
    },
    async reset(id?: string) {
      const proxy = createProxy(this.type)

      if (id && (this.type === CUSTOMER || this.type === ASSIGNMENT || this.type === SENDER_ADDRESS)) {
        await proxy.destroy(id)
      }

      this.resetState()
    },
    async remove(id: string) {
      const proxy = createProxy(this.type)
      const entityId = this.entityId ?? ''

      switch (this.type) {
        case CUSTOMER:
        case ASSIGNMENT:
        case SENDER_ADDRESS:
          await proxy.destroy(`${entityId}/${id}`)
          break
        default:
          await proxy.destroy(id)
          break
      }

      this.removeFromState(id)
    },
    create(payload: PriceFormData) {
      const proxy = createProxy(this.type)
      const entityId = this.entityId ?? ''
      const data: { prices: PriceFormData[] } = { prices: [] }

      if (this.type !== BASE) {
        data.prices = [...this.prices]
      }

      data.prices.push(payload)

      let request: Promise<any>

      switch (this.type) {
        case CUSTOMER:
        case ASSIGNMENT:
        case SENDER_ADDRESS:
          request = proxy.update(entityId, data)
          break
        default:
          request = (proxy as BasePriceProxy).create(data)
          break
      }

      return request.then(() => this.fetch(entityId))
    },
    fetch(id: string | Record<string, any> = '') {
      const isObjectId = typeof id === 'object'
      const proxy = createProxy(this.type, isObjectId ? id : undefined)
      let request: Promise<any>

      switch (this.type) {
        case CUSTOMER:
        case ASSIGNMENT:
        case SENDER_ADDRESS:
          request = proxy.find(isObjectId ? '' : id)
          break
        default:
          request = (proxy as BasePriceProxy).all()
          break
      }

      return request.then((result) => {
        this.setPrices(result.data)
        return result.data
      })
    },
    fetchDefault(id: string = '') {
      let request: Promise<any>

      switch (this.type) {
        case CUSTOMER:
          request = new CompanyPriceProxy().getDefaultPrices(id)
          break
        case ASSIGNMENT:
          request = new AssignmentPriceProxy().getDefaultPrices(id)
          break
        case SENDER_ADDRESS:
          request = new SenderAddressPriceProxy().getDefaultPrices(id)
          break
        default:
          request = new BasePriceProxy().all()
          break
      }

      return request.then((result) => result.data)
    },
    fetchDifferencePrices(companyId: string) {
      this.differencePrices[companyId] = []
      return new CompanyPriceProxy().getDiffPrices(companyId).then((result: AxiosCollection<PriceDifference>) => {
        this.differencePrices[companyId] = result.data
      })
    },
  },
})

export type PricesStore = ReturnType<typeof usePricesStore>
